import { env, pictureChoiceMaxDataUrlCharLength } from '@/config/env'
import { plainTextLength } from '../lib/plainTextLength'

/** US-IB-03 §10.3 — single source for save + inline copy. */

/** BFP-181 / BFP-509 — question `prompt` plain-text bounds (matches instrument-schema pattern). */
export const BUILDER_QUESTION_TEXT_MIN = 3
export const BUILDER_QUESTION_TEXT_MAX = 500

const POSITIVE_INT_MSG = 'Enter a positive whole number (1, 2, 3, …)'

/**
 * Min / Max selection fields (Figma 4398-5579 / 5682+): digits only, strip leading zeros.
 * Empty is allowed (optional limits).
 */
export function sanitizePositiveIntegerDigits(raw: string, maxLen = 9): string {
  const digits = raw.replaceAll(/\D/g, '').slice(0, maxLen)
  return digits.replaceAll(/^0+/g, '')
}

/** Non-empty value must be a positive integer (1, 2, 3, …). */
export function errorInvalidPositiveIntegerField(value: string): string | null {
  const t = value.trim()
  if (!t) return null
  if (!/^[1-9]\d*$/.test(t)) return POSITIVE_INT_MSG
  return null
}

/** Short text: when both limits are set as valid positive integers, enforce min ≤ max. */
export function errorShortTextLengthOrder(minLen: string, maxLen: string): string | null {
  const aStr = minLen.trim()
  const bStr = maxLen.trim()
  if (!aStr || !bStr) return null
  if (!/^[1-9]\d*$/.test(aStr) || !/^[1-9]\d*$/.test(bStr)) return null
  const a = Number(aStr)
  const b = Number(bStr)
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null
  if (a > b) return 'Minimum length cannot exceed maximum length'
  return null
}

/** When both min and max are non-empty valid positive ints, enforce min ≤ max. */
export function errorMinMaxOrder(min: string, max: string): string | null {
  const aStr = min.trim()
  const bStr = max.trim()
  if (!aStr || !bStr) return null
  const a = Number(aStr)
  const b = Number(bStr)
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null
  if (a > b) return 'Minimum selection cannot exceed maximum selection'
  return null
}

export function errorQuestionTextRequired(text: string): string | null {
  const len = plainTextLength(text)
  if (len === 0) return 'Question text is required'
  if (len < BUILDER_QUESTION_TEXT_MIN) {
    return 'Question text must be at least 3 characters'
  }
  if (len > BUILDER_QUESTION_TEXT_MAX) {
    return 'Question text cannot exceed 500 characters'
  }
  return null
}

/** Video builder — Figma heading copy (same 3–500 bounds as other types). */
export function errorQuestionHeadingRequired(text: string): string | null {
  const len = plainTextLength(text)
  if (len === 0) return 'Question heading is required'
  if (len < BUILDER_QUESTION_TEXT_MIN) {
    return 'Question heading must be at least 3 characters'
  }
  if (len > BUILDER_QUESTION_TEXT_MAX) {
    return 'Question heading cannot exceed 500 characters'
  }
  return null
}

/** Likert scale labels — 1-based point index in the inline / save message. */
export function errorLikertScaleLabelRequired(pointNumber: number): string {
  return `Enter a label for scale point ${pointNumber}`
}

export function errorOptionsNeedTwoNonEmpty(options: readonly string[]): string | null {
  const filled = options.map((s) => s.trim()).filter(Boolean)
  if (filled.length < 2) return 'Please add at least 2 options'
  return null
}

/** Picture choice: each row needs a non-empty image URL (data URL or https). */
export function countPictureOptionsWithImage(
  options: readonly { imageUrl: string }[],
): number {
  return options.filter((o) => o.imageUrl.trim().length > 0).length
}

export function errorPictureOptionsNeedTwoImages(
  options: readonly { imageUrl: string }[],
): string | null {
  if (countPictureOptionsWithImage(options) < 2) return 'Please add at least 2 images'
  return null
}

/** Every picture-choice row must have an uploaded image (Save / Continue). */
export function errorPictureOptionMissingImage(imageUrl: string): string | null {
  if (!imageUrl.trim()) return 'Each picture option must include an image'
  return null
}

/** Draft autosave may keep in-progress empty rows when at least two images exist. */
export function errorPictureRowsCompleteForSave(
  options: readonly { imageUrl: string }[],
): string | null {
  const minImagesErr = errorPictureOptionsNeedTwoImages(options)
  if (minImagesErr) return minImagesErr
  for (const row of options) {
    const missing = errorPictureOptionMissingImage(row.imageUrl)
    if (missing) return missing
  }
  return null
}

/** Ranking row counts when caption text or optional thumbnail is present (Figma 4399:9425). */
export function countRankingOptionsFilled(
  options: readonly { caption: string; imageUrl: string }[],
): number {
  return options.filter((o) => o.caption.trim().length > 0 || o.imageUrl.trim().length > 0).length
}

/** Ranking: at least two filled items (caption and/or image); thumbnails alone are valid. */
export function errorRankingOptionsNeedTwoNonEmpty(
  options: readonly { caption: string; imageUrl: string }[],
): string | null {
  if (countRankingOptionsFilled(options) < 2) return 'Please add at least 2 items to rank'
  return null
}

/** Max data-URL length allowed per image (from `VITE_PICTURE_CHOICE_MAX_IMAGE_BYTES` + base64 expansion). */
export const PICTURE_CHOICE_MAX_IMAGE_URL_CHARS = pictureChoiceMaxDataUrlCharLength(
  env.VITE_PICTURE_CHOICE_MAX_IMAGE_BYTES,
)

export function errorPictureImageTooLargeForDraft(imageUrl: string): string | null {
  const cap = pictureChoiceMaxDataUrlCharLength(env.VITE_PICTURE_CHOICE_MAX_IMAGE_BYTES)
  if (imageUrl.length > cap) {
    const mb = env.VITE_PICTURE_CHOICE_MAX_IMAGE_BYTES / (1024 * 1024)
    const mbText = Number.isInteger(mb) ? String(mb) : mb.toFixed(1)
    return `One or more images are too large to save in the draft. Use smaller files (max ${mbText} MB each).`
  }
  return null
}

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/

/** Non-empty value must be `yyyy-mm-dd` on a real calendar day (UTC). */
export function errorOptionalIsoDateField(value: string): string | null {
  const t = value.trim()
  if (!t) return null
  const m = ISO_DATE_RE.exec(t)
  if (!m) return 'Use format yyyy-mm-dd with a valid calendar date'
  const y = Number(m[1])
  const mo = Number(m[2])
  const d = Number(m[3])
  const dt = new Date(Date.UTC(y, mo - 1, d))
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) {
    return 'Use format yyyy-mm-dd with a valid calendar date'
  }
  return null
}

/** When both bounds are valid ISO dates, enforce earliest ≤ latest. */
export function errorDateRangeOrder(earliest: string, latest: string): string | null {
  const a = earliest.trim()
  const b = latest.trim()
  if (!a || !b) return null
  if (errorOptionalIsoDateField(a) || errorOptionalIsoDateField(b)) return null
  if (a > b) return 'Earliest date cannot be after latest date'
  return null
}

/** Non-empty trimmed options (same basis as “at least 2 options”). */
export function countFilledAnswerOptions(options: readonly string[]): number {
  return options.map((s) => s.trim()).filter(Boolean).length
}

/**
 * When `filledOptionCount` is set, min/max (if valid positive ints) cannot exceed it.
 * Run only after format + order checks pass.
 */
export function errorMinMaxVsAnswerOptionCount(
  min: string,
  max: string,
  filledOptionCount: number,
): string | null {
  if (filledOptionCount < 1) return null
  const aStr = min.trim()
  const bStr = max.trim()
  if (aStr && /^[1-9]\d*$/.test(aStr) && Number(aStr) > filledOptionCount) {
    return `Minimum selection cannot be greater than the number of options (${filledOptionCount})`
  }
  if (bStr && /^[1-9]\d*$/.test(bStr) && Number(bStr) > filledOptionCount) {
    return `Maximum selection cannot be greater than the number of options (${filledOptionCount})`
  }
  return null
}

/** True when value is a valid positive int strictly greater than `filledOptionCount`. */
export function selectionExceedsAnswerOptionCount(
  value: string,
  filledOptionCount: number,
): boolean {
  if (filledOptionCount < 1) return false
  if (errorInvalidPositiveIntegerField(value)) return false
  const t = value.trim()
  if (!t) return false
  return Number(t) > filledOptionCount
}

/** Format → order → optional cap vs filled answer options. */
export function errorMinMaxSelection(
  min: string,
  max: string,
  filledOptionCount?: number,
): string | null {
  const fmtMin = errorInvalidPositiveIntegerField(min)
  if (fmtMin) return fmtMin
  const fmtMax = errorInvalidPositiveIntegerField(max)
  if (fmtMax) return fmtMax
  const orderErr = errorMinMaxOrder(min, max)
  if (orderErr) return orderErr
  if (filledOptionCount !== undefined) {
    return errorMinMaxVsAnswerOptionCount(min, max, filledOptionCount)
  }
  return null
}
